import { Tabs, TabsList, TabsTrigger } from '@/shared/ui/tabs';
import { cn } from '@/shared/lib';
import type { DataTableView } from '@/shared/ui/data-table/types';
import { dataTableStyles } from '@/shared/ui/data-table/styles';

/**
 * The first row of the table: the saved views as a tab strip.
 *
 * Sits ABOVE the filter bar rather than next to it so the views feel like
 * navigation — they answer "which list am I looking at?" — and the filter bar
 * answers "how do I narrow this list?". Mixing them on one row collapses those
 * questions into one, and a user reaching for the right answer has to read
 * every control first.
 *
 * The count badge is informational. Tabs are still buttons, and a tab with a
 * count is still a single activatable control — the badge is read as part of
 * the tab's accessible name, not as a separate target.
 */
export function TabsRow({
  views,
  activeViewId,
  onViewChange,
  label,
}: {
  views: DataTableView[];
  activeViewId?: string;
  onViewChange?: (id: string) => void;
  label: string;
}) {
  if (views.length === 0) return null;

  const active = activeViewId ?? views[0]?.id;

  return (
    <Tabs
      className={dataTableStyles.tabsRow}
      value={active}
      onValueChange={(value) => onViewChange?.(String(value))}
    >
      <TabsList className={dataTableStyles.tabsRowList} aria-label={label}>
        {views.map((view) => (
          <TabsTrigger key={view.id} value={view.id} className={dataTableStyles.tabsRowTrigger}>
            <span>{view.label}</span>
            {view.count !== undefined ? (
              <span
                className={cn(
                  dataTableStyles.tabsRowCount,
                  view.id === active ? dataTableStyles.tabsRowCountActive : undefined,
                )}
                aria-label={`${view.count} ${view.count === 1 ? 'item' : 'items'}`}
              >
                {formatCount(view.count)}
              </span>
            ) : null}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}

/**
 * Compact count formatting for tab badges.
 *
 * Large numbers would push the tab strip wide and crowd the filter bar
 * beneath it; the rest of the badge text stays readable when we compress the
 * trailing digits into "k" / "m". The formatted value is rendered as a visual
 * hint; the screen reader still hears the full number via `aria-label`.
 */
function formatCount(count: number): string {
  if (count < 1_000) return String(count);
  if (count < 1_000_000) return `${Math.floor(count / 100) / 10}k`;
  return `${Math.floor(count / 100_000) / 10}m`;
}
